import { real_id } from 'src/helpers/interface_helpers';
import type { WebRTCStreamError } from '../types/webrtc-session';

/**
 * Transforms the SDP string to include only the specified codecs.
 * Performed by obfuscating the SDP to only the specified video and audio codecs.
 *
 * @todo: Define the audio codec to use.
 *
 * @param {string} sdp - The original SDP string.
 * @param {string} videoCodec - The payload type for the video codec.
 * @param {string} audioCodex - The payload type for the audio codec.
 * @returns {string} - The transformed SDP string with only the specified codecs.
 */
export function transformSdp(sdp: string, videoCodec: string, audioCodex: string): string {
	// console.log('original sdp', sdp);
	// const KEYS_TO_CLEAN = ['a=rtpmap:', 'a=fmtp:', 'a=rtcp-fb:'];
	// for (const prefix of KEYS_TO_CLEAN) {
	// 	sdp = sdp.replaceAll(
	// 		new RegExp(`${prefix}(?!${videoCodec}|${audioCodex}).*\\r\\n`, 'g'),
	// 		'',
	// 	);
	// }

	// // --- Force video m-line to only use PT ${videoCodec} ---
	// sdp = sdp.replace(
	// 	/m=video \d+ [\w/ ]+ [\d ]+/g,
	// 	`m=video 9 UDP/TLS/RTP/SAVPF ${videoCodec}`,
	// );

	// --- Force audio m-line to only use PT ${audioCodex} ---
	// sdp = sdp.replace(/m=audio \d+ [\w/ ]+ [\d ]+/g, `m=audio 9 UDP/TLS/RTP/SAVPF ${audioCodex}`);

	return sdp;
}

/**
 * Prepare ICE candidates for sending via RPC with Shelly-RPC conventional parameters.
 * @param candidates - The array of RTCIceCandidate objects to be prepared.
 * @returns An array of objects containing the candidate information.
 */
export function iceCandidatesToApiFormat(candidates: RTCIceCandidate[]): {
	candidate: string;
	sdp_m_id: string;
	sdp_m_line_index: number;
	username_fragment?: string;
}[] {
	return candidates.map((candidate) => ({
		candidate: candidate.candidate,
		sdp_m_id: candidate.sdpMid,
		sdp_m_line_index: candidate.sdpMLineIndex,
	}));
}

/**
 * Prepare ICE candidates received from the API for use as WebRTC RTCIceCandidate.
 * @param candidates - The array of candidate objects received from the API.
 * @returns An array of RTCIceCandidate objects.
 */
export function prepareRtcIceCandidates(
	candidates: {
		candidate: string;
		sdp_m_id: string;
		sdp_m_line_index: number;
		username_fragment?: string;
	}[],
): RTCIceCandidate[] {
	return candidates.map(
		(c) =>
			new RTCIceCandidate({
				sdpMid: c.sdp_m_id,
				sdpMLineIndex: c.sdp_m_line_index,
				candidate: c.candidate,
				usernameFragment: c?.username_fragment,
			}),
	);
}

/**
 * Prepare ICE servers for sending via RPC with RPC conventional parameters.
 * @param iceServers - The array of RTCIceServer objects to be prepared.
 * @returns An array of strings containing the prepared ICE server URLs.
 */
export function prepareICEServers(iceServers: RTCIceServer[]): string[] {
	return iceServers.map((server) => {
		const urls = server.urls as string;

		if (server.username && server.credential) {
			return urls.replace(':', `://${server.username}:${server.credential}@`);
		}

		return urls;
	});
}

/**
 * Identifies the shared session a player is attached to. The `_<n>` channel
 * suffix is stripped because those ids address the same physical camera, which
 * must not end up holding one stream slot per channel.
 */
export const webrtcSessionKey = (deviceId: string, streamId: number): string =>
	`${real_id(deviceId)}:${streamId}`;

/** The absence of a stream error, shared so players and sessions agree on it. */
export const NO_STREAM_ERROR: WebRTCStreamError = { heading: '', message: '' };

/** Cancels a pending timeout and returns the value its field should hold. */
export const clearTimer = (timer: ReturnType<typeof setTimeout> | null): null => {
	if (timer) {
		clearTimeout(timer);
	}
	return null;
};

/**
 * Ends every track and empties the stream. Only the owner of a `MediaStream`
 * may call this: stopping a shared stream blanks the video for every consumer.
 */
export const stopMediaStreamTracks = (stream: MediaStream) => {
	stream.getTracks().forEach((track) => {
		track.stop();
		stream.removeTrack(track);
	});
};

interface CameraRpcErrorOptions {
	/** The failure may have cost the camera one of its three stream slots. */
	slotConsuming?: boolean;
	/** The app had no cloud socket to send on, which says nothing about the camera. */
	awaitingCloud?: boolean;
}

/**
 * A camera RPC failure that also reports how it failed, so retry policy reads a
 * flag instead of inferring it from a message that translation can change.
 */
export class CameraRpcError extends Error {
	readonly slotConsuming: boolean;
	readonly awaitingCloud: boolean;

	constructor(
		message: string,
		{ slotConsuming = false, awaitingCloud = false }: CameraRpcErrorOptions = {},
	) {
		super(message);
		this.name = 'CameraRpcError';
		this.slotConsuming = slotConsuming;
		this.awaitingCloud = awaitingCloud;
	}
}

/**
 * Whether a `start_error` only means the cloud socket is not up yet, which is
 * retried silently instead of surfaced as a stream failure.
 */
export const isAwaitingCloudConnection = (error: unknown): boolean =>
	error instanceof CameraRpcError && error.awaitingCloud;

export const isSlotConsumingError = (error: unknown): boolean =>
	error instanceof CameraRpcError && error.slotConsuming;
